class BrowPrintBuilder {
  constructor(root) {
    this.root = root;
    this.system = this.read('[data-bp-system]');
    this.duo = this.read('[data-bp-duo]');
    this.pigments = this.read('[data-bp-pigment]');
    this.oil = JSON.parse(root.querySelector('[data-bp-oil]').textContent);
    this.bind();
    this.update();
    this.initCarousels();
    this.initAccordions();
    this.initReveals();
    this.initSticky();
  }
  read(selector) {
    const raw = this.root.querySelector(selector).textContent.trim();
    return raw ? JSON.parse(`[${raw}]`) : [];
  }
  value(name) { return this.root.querySelector(`[name="${name}"]:checked`)?.value; }
  bind() {
    this.root.addEventListener('change', (event) => {
      if (event.target.matches('[name="bp-offer"], [data-addon]')) this.update();
      if (event.target.matches('[data-addon="pigment"]')) this.root.querySelector('[data-extra-pigment]').hidden = !event.target.checked;
    });
    this.root.querySelectorAll('[data-bp-submit]').forEach(button => button.addEventListener('click', () => this.submit(button.dataset.bpDestination || 'cart')));
    this.root.querySelectorAll('[data-gallery-dot]').forEach(button => button.addEventListener('click', () => {
      this.root.querySelectorAll('.bp-gallery figure').forEach((figure, index) => figure.classList.toggle('is-active', index === Number(button.dataset.galleryDot)));
    }));
    this.root.querySelectorAll('[data-bp-back-to-buy]').forEach(link => link.addEventListener('click', event => {
      event.preventDefault();
      this.root.querySelector('.bp-buybox')?.scrollIntoView({behavior:'smooth', block:'start'});
    }));
  }
  initCarousels() {
    this.root.querySelectorAll('[data-bp-carousel]').forEach(carousel => {
      const track = carousel.querySelector('[data-bp-track]');
      const slides = [...carousel.querySelectorAll('[data-bp-slide]')];
      const dots = carousel.querySelector('[data-bp-dots]');
      if (!track || !slides.length) return;
      const go = index => slides[Math.max(0, Math.min(index, slides.length - 1))].scrollIntoView({behavior:'smooth', block:'nearest', inline:'start'});
      const current = () => {
        const left = track.getBoundingClientRect().left;
        return slides.reduce((best, slide, index) => Math.abs(slide.getBoundingClientRect().left - left) < best.distance ? {index,distance:Math.abs(slide.getBoundingClientRect().left - left)} : best, {index:0,distance:Infinity}).index;
      };
      if (dots) {
        slides.forEach((slide,index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
          dot.classList.toggle('is-active', index === 0);
          dot.addEventListener('click', () => go(index));
          dots.appendChild(dot);
        });
      }
      carousel.querySelector('[data-bp-prev]')?.addEventListener('click', () => go(current() - 1));
      carousel.querySelector('[data-bp-next]')?.addEventListener('click', () => go(current() + 1));
      let ticking = false;
      track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const active = current();
          dots?.querySelectorAll('button').forEach((dot,index) => dot.classList.toggle('is-active', index === active));
          ticking = false;
        });
      }, {passive:true});
    });
  }
  initAccordions() {
    const items = this.root.querySelectorAll('.bp-faq details');
    items.forEach(item => item.addEventListener('toggle', () => {
      if (!item.open) return;
      items.forEach(other => { if (other !== item) other.open = false; });
    }));
  }
  initReveals() {
    const items = this.root.querySelectorAll('.bp-reveal');
    if (!('IntersectionObserver' in window)) {
      items.forEach(item => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    }), {rootMargin:'0px 0px -8% 0px', threshold:.08});
    items.forEach(item => observer.observe(item));
  }
  initSticky() {
    const sticky = this.root.querySelector('[data-bp-sticky]');
    const primaryButton = this.root.querySelector('.bp-buybox .bp-atc--buy');
    if (!sticky || !primaryButton) return;
    const updateSticky = () => sticky.classList.toggle('is-visible', primaryButton.getBoundingClientRect().bottom < 0);
    updateSticky();
    window.addEventListener('scroll', updateSticky, {passive:true});
    window.addEventListener('resize', updateSticky, {passive:true});
  }
  update() {
    const duo = this.value('bp-offer') === 'duo';
    this.root.querySelector('[data-system-config="2"]').hidden = !duo;
    const selectedOffer = this.root.querySelector('[name="bp-offer"]:checked');
    this.root.querySelectorAll('[data-bp-price]').forEach(node => node.textContent = selectedOffer?.dataset.price || '');
    const sticky = this.root.querySelector('[data-bp-sticky-offer]');
    if (sticky) sticky.textContent = duo ? '2 independently configured Systems' : '1 complete System';
  }
  findSystem(shape) { return this.system.find(variant => variant.shape === shape); }
  findPigment(colour) { return this.pigments.find(variant => variant.colour === colour); }
  findDuo(first, second) {
    const normalise = value => value.toLowerCase().replace(/[^a-z]/g, '');
    return this.duo.find(variant => {
      const pair = normalise(variant.pair);
      return pair.includes(normalise(first)) && pair.includes(normalise(second));
    });
  }
  async submit(destination = 'cart') {
    const isDuo = this.value('bp-offer') === 'duo';
    const shape1 = this.value('shape-1');
    const colour1 = this.value('colour-1');
    const shape2 = this.value('shape-2');
    const colour2 = this.value('colour-2');
    const main = isDuo ? this.findDuo(shape1, shape2) : this.findSystem(shape1);
    const error = this.root.querySelector('[data-bp-error]');
    const unavailable = !main?.available;
    if (unavailable) {
      error.textContent = 'This selection is currently unavailable. Please choose another option or contact support@soraderma.com.';
      error.hidden = false;
      return;
    }
    const properties = isDuo
      ? {
          'Offer':'2 BrowPrint™ Systems',
          'System 1 shape':shape1,
          'System 1 pigment':colour1,
          'System 2 shape':shape2,
          'System 2 pigment':colour2,
          'Included cleanser':'2 × BrowPrint™ Plate Cleansing Oil',
        }
      : {
          'Offer':'1 BrowPrint™ System',
          'Shape':shape1,
          'Included pigment':colour1,
          'Included cleanser':'1 × BrowPrint™ Plate Cleansing Oil',
        };
    const items = [{id:main.id,quantity:1,properties}];
    const add = (id, quantity, properties) => {
      const found = items.find(item => item.id === id && JSON.stringify(item.properties || {}) === JSON.stringify(properties || {}));
      if (found) found.quantity += quantity; else items.push({id,quantity,properties});
    };
    if (this.root.querySelector('[data-addon="pigment"]').checked) add(Number(this.root.querySelector('[data-extra-pigment] select').value),1,{'Add-on':'Paid extra pigment','Pigment shade':this.root.querySelector('[data-extra-pigment] select').selectedOptions[0].textContent.trim()});
    if (this.root.querySelector('[data-addon="oil"]').checked) add(this.oil.id,1,{'Add-on':'Paid extra cleansing oil'});
    const buttons = this.root.querySelectorAll('[data-bp-submit]');
    buttons.forEach(button => button.disabled = true);
    error.hidden = true;
    try {
      const lines = items.map(item => ({
        merchandiseId: String(item.id),
        quantity: item.quantity,
        attributes: Object.entries(item.properties || {}).map(([key, value]) => ({key, value: String(value)})),
      }));
      let resolveCartUpdate;
      let rejectCartUpdate;
      const cartUpdatePromise = new Promise((resolve, reject) => {
        resolveCartUpdate = resolve;
        rejectCartUpdate = reject;
      });
      const cartUpdateEvent = new CustomEvent('shopify:cart:lines-update', {
        bubbles: true,
        detail: {source: 'browprint-builder'},
      });
      const eventLines = lines.map(line => ({merchandiseId: line.merchandiseId, quantity: line.quantity}));
      Object.assign(cartUpdateEvent, {action: 'add', context: 'product', lines: eventLines, promise: cartUpdatePromise});
      this.root.dispatchEvent(cartUpdateEvent);

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
          body: JSON.stringify({items}),
        });
        if (!response.ok) throw new Error((await response.json()).description || 'Unable to add this selection.');
        const ajaxCartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, {
          headers: {'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
        });
        if (!ajaxCartResponse.ok) throw new Error('Unable to refresh the cart.');
        const ajaxCart = await ajaxCartResponse.json();
        resolveCartUpdate({
          cart: {
            id: ajaxCart.token,
            totalQuantity: ajaxCart.item_count,
            cost: {totalAmount: {amount: (ajaxCart.total_price / 100).toFixed(2), currencyCode: ajaxCart.currency}},
            lines: ajaxCart.items.map(line => ({
              id: line.key,
              quantity: line.quantity,
              cost: {totalAmount: {amount: (line.final_line_price / 100).toFixed(2), currencyCode: ajaxCart.currency}},
            })),
            discountCodes: (ajaxCart.cart_level_discount_applications || []).map(discount => ({code: discount.title, applicable: true})),
          },
        });
      } catch (cartException) {
        rejectCartUpdate(cartException);
        const cartErrorEvent = new CustomEvent('shopify:cart:error', {
          bubbles: true,
          detail: {source: 'browprint-builder'},
        });
        Object.assign(cartErrorEvent, {error: cartException.message, code: 'SERVICE_UNAVAILABLE'});
        this.root.dispatchEvent(cartErrorEvent);
        throw cartException;
      }
      window.location.href = `${window.Shopify.routes.root}${destination === 'checkout' ? 'checkout' : 'cart'}`;
    } catch (exception) {
      error.textContent = exception.message;
      error.hidden = false;
      buttons.forEach(button => button.disabled = false);
      error.scrollIntoView({behavior:'smooth',block:'center'});
    }
  }
}
document.addEventListener('DOMContentLoaded', () => document.querySelectorAll('[data-browprint-builder]').forEach(root => new BrowPrintBuilder(root)));
